/**
 * The content of one window inside its own error boundary: a crash shows an error card in
 * that window only (logged once to the core), with a button to reload the window.
 */
import { ErrorBoundary, Show, createMemo, onMount, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useApp } from "../app/context";

/** The content of one window, isolated by an error boundary. */
export function WindowBody(props: { window: string }): JSX.Element {
  const { wm } = useApp();
  // A memo: a plugin's window can be placed before its plugin is known, then appear.
  const def = createMemo(() => wm.def(props.window));
  const crashed = createMemo(() => wm.crashed().has(props.window));
  return (
    <div class="win-body" data-window={props.window}>
      <ErrorBoundary fallback={(error, reset) => <CrashCard window={props.window} error={error} reset={reset} />}>
        <Show when={!crashed()} fallback={<Thrower window={props.window} />}>
          <Show when={def()} fallback={<p class="muted pad">{props.window.startsWith("plugin:") ? "This plugin window is not available: its plugin is not installed, turned on or approved." : `Unknown window “${props.window}”.`}</p>}>
            {(d) => <Dynamic component={d().component} window={props.window} />}
          </Show>
        </Show>
      </ErrorBoundary>
    </div>
  );
}

/** Throws on render: used by the debug crash switch to prove windows are isolated. */
function Thrower(props: { window: string }): JSX.Element {
  throw new Error(`debug crash requested for the ${props.window} window`);
}

/** Shown instead of a window that crashed. Logs the crash once and offers a reload. */
function CrashCard(props: { window: string; error: unknown; reset: () => void }): JSX.Element {
  const { actions, wm } = useApp();
  const message = props.error instanceof Error ? props.error.message : String(props.error);
  onMount(() => actions.log(props.window, "error", `window crashed: ${message}`));
  return (
    <div class="crash" role="alert" data-crashed={props.window}>
      <strong>This window crashed.</strong>
      <p class="mono">{message}</p>
      <p class="muted">The rest of the app and the core are not affected. The error was written to the log.</p>
      <button
        type="button"
        class="btn"
        onClick={() => {
          wm.setCrashed(props.window, false);
          props.reset();
        }}
      >
        Reload window
      </button>
    </div>
  );
}
